/**
 * THE ACCOUNT'S SIDE (#708): what the module reads of `GET /api/me`, and the one write it makes to the server.
 *
 * READ: `me` is the TanStack Query cache's `['me']` entry, which `useMe` keeps (on mount, after a write); the module
 * subscribes to the cache instead of asking for `me` itself, so it adds no request. It reads two members and nothing
 * else: `consents.analytics` and `analytics_user_ref`. `user_id`, the account number, is never read, and so can never
 * reach the data layer. Without a session the account does not count (a visitor); with one whose `me` has not
 * answered, nothing is decided yet.
 *
 * WRITE: the reconciliation's rows 4 and 5 (an answer in the cookie, an account never asked) are recorded here with
 * client `web_visitor` (AA-D6, rulings R1 and R3): one POST, once per page life for an answer and a version, and the
 * server's answer put into `['me']` the way `api/consents.ts` does. Answers given on a screen while signed in are not
 * this file's: they go through `api/consents.ts` with client `web`.
 */
import type { QueryClient } from '@tanstack/react-query'

import { hasSession } from '../api/auth'
import { apiFetch } from '../api/client'
import type { components } from '../api/generated/schema'
import type { Answer, ConsentState } from './consent'
import type { AccountRead } from './loader'

type MeOut = components['schemas']['MeOut']

/** The client value of an answer the account takes from the visitor's cookie at sign-in. */
export const VISITOR_CLIENT = 'web_visitor'

const ME = ['me'] as const

/** The account as the cache holds it now. */
export function readAccount(queryClient: QueryClient): AccountRead {
  const query = queryClient.getQueryCache().find<MeOut>({ queryKey: ME, exact: true })
  const me = query?.state.data
  if (me !== undefined) {
    return {
      account: { kind: 'ready', state: me.consents?.analytics },
      userRef: me.analytics_user_ref,
      updatedAt: query?.state.dataUpdatedAt ?? 0,
    }
  }
  return { account: hasSession() ? { kind: 'loading' } : { kind: 'visitor' }, userRef: null, updatedAt: 0 }
}

/** Calls *listener* whenever the cache's `['me']` changes. */
export function watchAccount(queryClient: QueryClient, listener: () => void): () => void {
  return queryClient.getQueryCache().subscribe((event) => {
    const key = event.query.queryKey
    if (key.length === 1 && key[0] === 'me' && event.type === 'updated') listener()
  })
}

/** Asks `GET /api/me` again: the ref after a grant, and the account after another tab changed the cookie. */
export function refreshAccount(queryClient: QueryClient): void {
  void queryClient.invalidateQueries({ queryKey: ME, exact: true })
}

/**
 * Records the browser's answer on the account, with client `web_visitor`. A failure is left for the next page load
 * (ruling R3: never sent again on every read of `me`); the browser's own answer still decides what loads.
 */
export function recordVisitorAnswer(queryClient: QueryClient, answer: Answer): void {
  const action = answer.choice === 'g' ? 'grant' : 'decline'
  apiFetch<ConsentState>(`/api/me/consents/analytics/${action}`, {
    method: 'POST',
    body: JSON.stringify({ version: answer.version, client: VISITOR_CLIENT }),
  })
    .then(async (state) => {
      // As `api/consents.ts`: a `me` read that left before this answer was stored would put the old state back.
      await queryClient.cancelQueries({ queryKey: ME, exact: true })
      queryClient.setQueryData<MeOut>(ME, (me) => (me ? { ...me, consents: { ...me.consents, analytics: state } } : me))
      // The ref is the server's to hand out, and only for a grant it holds: ask `me` for it now.
      if (answer.choice === 'g') refreshAccount(queryClient)
    })
    .catch(() => {})
}
